import React from 'react';
import { connect } from 'react-redux';
import { TextField, Checkbox, Typography } from '@mui/material';
import { AUTH_LEVELS } from '../auth/Authorized';
import { Row } from '../util/Page';
import {
  associateBuildingsAction,
  upsertDeploymentAction,
  upsertDeploymentCollectorAction,
  upsertDeploymentConfigAction,
} from '../../redux/actions/deployments';
import { DeploymentConfigLoader } from '../loaders/deployments';
import Selector from '../util/Selector';
import InlineTextEdit from '../util/InlineTextEdit';
import OrganizationSelector from '../userMgmt/OrganizationSelector';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import ApiKeyLoader from '../apiMgmt/ApiKeyLoader';
import BuildingMultiSelector from '../buildingselector/BuildingMultiSelector';

import './deployment-config.css';

const NetworkSelector = ({ collector, selected, onChange, disabled }) => {
  const ipFilter = (ip) => {
    if (ip.startsWith('127.0.0.1')) return false;
    if (ip.startsWith(collector.private_ip)) return false;
    return true;
  };
  const ips = collector?.device_ips?.filter(ipFilter) || [];
  if (selected && ips.indexOf(selected) === -1) {
    ips.push(selected);
  }
  return (
    <Selector
      options={ips.map((ip) => ({
        id: ip,
        key: ip,
        display: ip,
      }))}
      showNullOption
      selected={selected ?? ''}
      onChange={({ target: { value } }) => onChange(value)}
      disabled={disabled}
    />
  );
};

const DeploymentConfig = ({
  deployment: d,
  collectors,
  config,
  apiKey,
  canWrite,
  updateDeployment,
  updateCollector,
  updateConfig,
  associateBuildings,
}) => (
  <>
    <DeploymentConfigLoader site={d.site} />
    <OrganizationsLoader />
    <ApiKeyLoader />

    <Row>
      Organization: <OrganizationSelector disabled selected={`${d.org_id}`} />
      {/*  eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label>
        Active?
        <Checkbox
          checked={d.active}
          onChange={(e) => updateDeployment({ ...d, active: e.target.checked })}
          disabled={!canWrite}
        />
      </label>
    </Row>
    <Row>
      <div>
        <Typography variant="h6">Associated Buildings:</Typography>
        <BuildingMultiSelector
          selected={d.buildings}
          orgId={d.org_id}
          onValueChange={canWrite ? (buildings) => associateBuildings(d.site, buildings) : () => {}}
          noneText="No buildings"
        />
      </div>
    </Row>
    {collectors?.map((collector) => (
      <Row className="collector-border" key={collector.private_ip}>
        <div className="section">
          <div>
            Active Collector?
            <Checkbox
              id="active_collector"
              checked={collector.active}
              onChange={(e) => updateCollector({ ...collector, active: e.target.checked })}
              disabled={!canWrite}
            />
          </div>

          <div>
            <TextField
              label="API Key"
              value={apiKey ? apiKey.name : collector.api_key}
              disabled
              fullWidth
            />
          </div>

          <div>
            <TextField label="Private IP" value={collector.private_ip} disabled fullWidth />
          </div>

          <div>
            <TextField label="Wireguard Pubkey" value={collector.wg_pubkey} disabled fullWidth />
          </div>
        </div>

        <div className="section">
          <div>
            <TextField label="Type" value={d.type} disabled fullWidth />
          </div>
          {d.type === 'bacnet' && (
            <div>
              <div>
                <Typography>Network:</Typography>
                <NetworkSelector
                  collector={collector}
                  selected={config.bacnet?.network ?? null}
                  onChange={(network) => updateConfig(d.site, { network })}
                  disabled={!canWrite}
                />
              </div>
              <div>
                <InlineTextEdit
                  label="Port"
                  type="number"
                  placeholder="47808"
                  value={config.bacnet?.port}
                  onValueChange={(port) => port && updateConfig(d.site, { port })}
                  disabled={!canWrite}
                  inputProps={{ min: 1025, max: 65_535 }}
                />
              </div>
              <div>
                <InlineTextEdit
                  label="Polling frequency, seconds"
                  type="number"
                  placeholder="60"
                  value={config.bacnet?.poll_freq_secs}
                  onValueChange={(s) => s && updateConfig(d.site, { poll_freq_secs: s })}
                  disabled={!canWrite}
                  inputProps={{ min: 10, max: 3600 }}
                />
              </div>
              <div>
                <InlineTextEdit
                  label="BBMD IP"
                  type="text"
                  value={config.bacnet?.bbmd_ip}
                  onValueChange={(bbmdIp) => bbmdIp && updateConfig(d.site, { bbmd_ip: bbmdIp })}
                  disabled={!canWrite}
                />
              </div>
              <div>
                <InlineTextEdit
                  label="BBMD TTL"
                  type="number"
                  placeholder="900"
                  value={config.bacnet?.bbmd_ttl}
                  onValueChange={(bbmdTtl) =>
                    bbmdTtl && updateConfig(d.site, { bbmd_ttl: bbmdTtl })
                  }
                  disabled={!canWrite}
                  inputProps={{ min: 1, max: 1800 }}
                />
              </div>
            </div>
          )}
        </div>
      </Row>
    ))}
  </>
);

export default connect(
  (
    {
      apiKeys: { userKeys },
      entities: {
        deployments: { configs },
      },
      user: { userAuthLevel },
    },
    { deployment }
  ) => ({
    apiKey: userKeys.find(({ hash }) => hash === deployment.api_key),
    collectors: configs[deployment.site]?.collectors ?? [],
    config: configs[deployment.site]?.configs ?? {},
    canWrite: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
  }),
  (dispatch) => ({
    associateBuildings: (site, buildings) => dispatch(associateBuildingsAction(site, buildings)),
    updateDeployment: (deployment) => dispatch(upsertDeploymentAction(deployment)),
    updateCollector: (collector) => dispatch(upsertDeploymentCollectorAction(collector)),
    updateConfig: (site, config) => dispatch(upsertDeploymentConfigAction(site, config)),
  })
)(DeploymentConfig);
